import { useState, type FormEventHandler } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLogin, useRegister, useUsernameAvailability } from '@/hooks/queries'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { ApiError } from '@/api/client'
import type { UsernameAvailability } from '@/api/endpoints'
import { queryKeys } from '@/api/query-keys'
import { notifyError } from '@/lib/notify'
import { discordLoginUrl, startOAuth } from '@/lib/oauth'
import { saveReturnPath } from '@/lib/return-path'
import { cn } from '@/lib/cn'

interface AuthProps {
  onClose: () => void
}

type Mode = 'login' | 'register'

const MIN_PASSWORD_LENGTH = 8

type UsernameStatus =
  | { state: 'idle' }
  | { state: 'checking' }
  | { state: 'available' }
  | { state: 'unavailable'; message: string }
  | { state: 'unknown' }

/**
 * What to show under the username field. `typed` is the live input and
 * `checked` the debounced value the query ran for; until they agree the
 * answer on screen would be about a name the user has already changed.
 */
function usernameStatus(
  typed: string,
  checked: string,
  result: UsernameAvailability | undefined,
  fetching: boolean,
  failed: boolean,
): UsernameStatus {
  const name = typed.trim()
  if (!name) return { state: 'idle' }
  if (name !== checked.trim() || fetching) return { state: 'checking' }
  // The check itself failed. Say so rather than going blank, which reads as
  // the check never having run; /auth/register still enforces the rules.
  if (failed) return { state: 'unknown' }
  // The query is keyed by the name, so `result` is always about this one --
  // no need to match `result.username`, which would blank the hint if the API
  // ever normalised the name it echoes back.
  if (!result) return { state: 'idle' }
  if (result.available) return { state: 'available' }
  return { state: 'unavailable', message: result.message ?? 'That username is not available.' }
}

/**
 * Port of components/Auth.vue.
 *
 * The live Quasar build had the username/password and registration forms
 * commented out, leaving only "Sign in with Discord" for the Spawn World key
 * hand-out. Both are restored here — the /auth/login and /auth/register
 * endpoints the Vuex store called are still part of the API surface — with the
 * two modes as tabs rather than a link that swaps the whole panel out.
 */
export function Auth({ onClose }: AuthProps) {
  const [mode, setMode] = useState<Mode>('login')
  const [leaving, setLeaving] = useState(false)

  const queryClient = useQueryClient()
  const login = useLogin()
  const register = useRegister()

  const [identifier, setIdentifier] = useState('')
  const [username, setUsername] = useState('')
  const [nickname, setNickname] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const debouncedUsername = useDebouncedValue(username, 350)
  const availability = useUsernameAvailability(mode === 'register' ? debouncedUsername : '')
  const usernameCheck = usernameStatus(
    username,
    debouncedUsername,
    availability.data,
    availability.isFetching,
    availability.isError,
  )

  const busy = login.isPending || register.isPending || leaving

  // The Discord handoff leaves the app on web and desktop, so the button
  // reports that it is going rather than sitting inert until the page unloads.
  const signInWithDiscord = () => {
    setLeaving(true)
    saveReturnPath(window.location.pathname + window.location.search)
    void startOAuth(discordLoginUrl()).finally(() => setLeaving(false))
  }

  const onLogin: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault()
    if (!identifier.trim()) return notifyError('Enter your username or email.')
    if (!password) return notifyError('Enter your password.')

    await login.mutateAsync({ username: identifier.trim(), password })
    onClose()
  }

  const onRegister: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault()
    if (!username.trim()) return notifyError('Choose a username.')
    if (usernameCheck.state === 'unavailable') return notifyError(usernameCheck.message)
    if (!email.trim() || !email.includes('@')) return notifyError('Enter a valid email address.')
    if (password.length < MIN_PASSWORD_LENGTH) {
      return notifyError(`Passwords need at least ${MIN_PASSWORD_LENGTH} characters.`)
    }
    if (password !== confirmPassword) return notifyError('Passwords do not match.')

    try {
      await register.mutateAsync({
        username: username.trim(),
        email: email.trim(),
        password,
        // The API treats nickname as optional; only send one if it was given.
        ...(nickname.trim() ? { nickname: nickname.trim() } : {}),
      })
    } catch (error) {
      // The toast already shows the API's message. If the name was taken in
      // the meantime, also update the hint under the field.
      const code =
        error instanceof ApiError
          ? (error.data as { error?: string } | undefined)?.error
          : undefined
      if (code === 'username_taken') {
        const name = username.trim()
        queryClient.setQueryData<UsernameAvailability>(queryKeys.usernameAvailable(name), {
          username: name,
          available: false,
          error: code,
          message: (error as ApiError).message,
        })
      }
      return
    }
    onClose()
  }

  return (
    <div className="overlay-wrapper auth-panel">
      <div className="header">
        <div className="header-content">
          <div className="title">
            <div className="logo">
              <img src="/icons/logo.png" alt="" />
            </div>
            <div className="text">JustLoot.me</div>
          </div>
        </div>
      </div>

      <div className="auth-tabs" role="tablist" aria-label="Sign in or create an account">
        {(
          [
            ['login', 'Sign in'],
            ['register', 'Create account'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={mode === value}
            className={cn('auth-tab', mode === value && 'active')}
            onClick={() => setMode(value)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="content">
        {mode === 'login' ? (
          <form onSubmit={onLogin} noValidate>
            <input
              className="input-field"
              placeholder="Username or email"
              aria-label="Username or email"
              autoComplete="username"
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
            />
            <input
              className="input-field"
              placeholder="Password"
              aria-label="Password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <div className="login">
              <button type="submit" className="btn btn-sheen" disabled={busy}>
                {login.isPending ? <span className="spinner-inline" aria-hidden="true" /> : null}
                {login.isPending ? 'Signing in…' : 'Sign in'}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={onRegister} noValidate>
            <input
              className="input-field"
              placeholder="Username"
              aria-label="Username"
              autoComplete="username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              aria-invalid={usernameCheck.state === 'unavailable' || undefined}
              aria-describedby="register-username-status"
            />
            <div
              id="register-username-status"
              className={cn('auth-hint', 'auth-field-status', usernameCheck.state)}
              aria-live="polite"
            >
              {usernameCheck.state === 'checking' && 'Checking availability…'}
              {usernameCheck.state === 'available' && 'Username is available.'}
              {usernameCheck.state === 'unavailable' && usernameCheck.message}
              {usernameCheck.state === 'unknown' &&
                "Couldn't check this username right now. It will be checked when you sign up."}
            </div>
            <input
              className="input-field"
              placeholder="Display name (optional)"
              aria-label="Display name"
              autoComplete="nickname"
              value={nickname}
              onChange={(event) => setNickname(event.target.value)}
            />
            <input
              className="input-field"
              placeholder="Email"
              aria-label="Email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <input
              className="input-field"
              placeholder="Password"
              aria-label="Password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <input
              className="input-field"
              placeholder="Confirm password"
              aria-label="Confirm password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
            <div className="auth-hint">At least {MIN_PASSWORD_LENGTH} characters.</div>
            <div className="login">
              <button type="submit" className="btn btn-sheen" disabled={busy}>
                {register.isPending ? <span className="spinner-inline" aria-hidden="true" /> : null}
                {register.isPending ? 'Creating…' : 'Create account'}
              </button>
            </div>
          </form>
        )}

        <div className="divider">
          <div className="line" />
          <span className="divider-text">OR</span>
          <div className="line" />
        </div>

        <div className="oauth">
          <button
            type="button"
            className="btn discord btn-sheen"
            onClick={signInWithDiscord}
            disabled={busy}
          >
            {leaving ? (
              <span className="spinner-inline" aria-hidden="true" />
            ) : (
              <svg
                className="discord-mark"
                viewBox="0 0 24 18"
                aria-hidden="true"
                fill="currentColor"
              >
                <path d="M20.32 1.52A19.8 19.8 0 0 0 15.43 0c-.21.38-.46.9-.63 1.31a18.3 18.3 0 0 0-5.6 0C9.03.9 8.77.38 8.56 0a19.74 19.74 0 0 0-4.9 1.52C.56 6.15-.34 10.67.11 15.12A19.94 19.94 0 0 0 6.15 18c.49-.66.92-1.37 1.3-2.11-.71-.27-1.4-.6-2.04-.99.17-.13.34-.26.5-.4a14.2 14.2 0 0 0 12.19 0c.16.14.33.27.5.4-.65.39-1.33.72-2.05.99.37.74.81 1.45 1.3 2.11a19.9 19.9 0 0 0 6.04-2.88c.53-5.16-.9-9.64-3.57-13.6ZM8.02 12.38c-1.18 0-2.15-1.08-2.15-2.4 0-1.32.95-2.4 2.15-2.4 1.21 0 2.18 1.08 2.16 2.4 0 1.32-.95 2.4-2.16 2.4Zm7.95 0c-1.18 0-2.15-1.08-2.15-2.4 0-1.32.95-2.4 2.15-2.4 1.21 0 2.18 1.08 2.16 2.4 0 1.32-.95 2.4-2.16 2.4Z" />
              </svg>
            )}
            {leaving ? 'Redirecting…' : 'Sign in with Discord'}
          </button>
        </div>

        <p className="auth-legal">
          Discord is the fastest way in — it is also how giveaway entries are verified.
        </p>
      </div>
    </div>
  )
}
